import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fleetAPI from '../api/fleetAPI';
import dashboardAPI from '../api/dashboardAPI';

// Async thunks for fetching data
export const fetchDashboardOverview = createAsyncThunk(
  'clientDashboard/fetchOverview',
  async (timeFilter = 'day') => {
    try {
      // Fetch all data in parallel
      const [vehicles, drivers, devices, events, overview] = await Promise.all([
        fleetAPI.getMyVehicles(),
        fleetAPI.getMyDrivers(),
        fleetAPI.getMyDevices(),
        fleetAPI.getMyEvents({ period: timeFilter }),
        fleetAPI.getFleetOverview(),
      ]);

      // Debug logging
      console.log('Dashboard API Responses:', {
        vehicles: vehicles,
        drivers: drivers,
        devices: devices,
        events: events,
        overview: overview,
      });

      // Process vehicles data
      const vehiclesData = vehicles.success ? vehicles.data : [];
      const totalVehicles = Array.isArray(vehiclesData) ? vehiclesData.length : 0;
      const activeVehicles = Array.isArray(vehiclesData)
        ? vehiclesData.filter((v) => v.status === 'active' || v.is_active).length
        : 0;

      // Process drivers data
      const driversData = drivers.success ? drivers.data : {};
      // Handle different response formats from the API
      const driversList = Array.isArray(driversData)
        ? driversData
        : driversData.drivers || driversData.results || driversData.data || [];
      const totalDrivers = Array.isArray(driversList) ? driversList.length : 0;
      const activeDrivers = Array.isArray(driversList)
        ? driversList.filter((d) => d.status === 'active' || d.is_active).length
        : 0;

      // Process devices data
      const devicesData = devices.success ? devices.data : [];
      const totalDevices = Array.isArray(devicesData) ? devicesData.length : 0;

      // Process events data
      const eventsData = events.success ? events.data : {};
      const eventsList = Array.isArray(eventsData)
        ? eventsData
        : eventsData.events || eventsData.results || eventsData.data || [];

      // Count events by type
      const eventCounts = Array.isArray(eventsList)
        ? eventsList.reduce((acc, event) => {
            const type = event.event_type || event.type || event.alert_type || 'Other';
            acc[type] = (acc[type] || 0) + 1;
            return acc;
          }, {})
        : {};

      // Find top event type
      const topEventType =
        Object.keys(eventCounts).length > 0
          ? Object.keys(eventCounts).reduce((a, b) => (eventCounts[a] > eventCounts[b] ? a : b))
          : 'No Events';

      // Calculate today's events (events from last 24 hours)
      const now = new Date();
      const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
      const todayEvents = Array.isArray(eventsList)
        ? eventsList.filter((e) => {
            const eventDate = new Date(e.created_at || e.timestamp || e.event_time);
            return eventDate > oneDayAgo;
          }).length
        : 0;

      // Calculate risk scores - only include items with real risk scores
      const vehicleRiskScores = Array.isArray(vehiclesData)
        ? vehiclesData
            .filter((v) => v.risk_score !== undefined && v.risk_score !== null)
            .map((v) => ({
              id: v.id,
              registration:
                v.registration || v.vehicle_registration || v.license_plate || 'Unknown',
              riskScore: v.risk_score,
            }))
            .sort((a, b) => b.riskScore - a.riskScore)
        : [];

      const driverRiskScores = Array.isArray(driversList)
        ? driversList
            .filter((d) => d.risk_score !== undefined && d.risk_score !== null)
            .map((d) => ({
              id: d.id,
              name: d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Unknown',
              riskScore: d.risk_score,
            }))
            .sort((a, b) => b.riskScore - a.riskScore)
        : [];

      const highestRiskVehicle =
        vehicleRiskScores.length > 0
          ? vehicleRiskScores[0]
          : { registration: 'No Vehicles Loaded', riskScore: null };
      const lowestRiskVehicle =
        vehicleRiskScores.length > 0
          ? vehicleRiskScores[vehicleRiskScores.length - 1]
          : { registration: 'No Vehicles Loaded', riskScore: null };
      const highestRiskDriver =
        driverRiskScores.length > 0
          ? driverRiskScores[0]
          : { name: 'No Drivers Loaded', riskScore: null };
      const lowestRiskDriver =
        driverRiskScores.length > 0
          ? driverRiskScores[driverRiskScores.length - 1]
          : { name: 'No Drivers Loaded', riskScore: null };

      return {
        overview: {
          totalVehicles,
          activeVehicles,
          totalDrivers,
          activeDrivers,
          totalDevices,
          newEventsToday: todayEvents,
          topEventType: topEventType || 'No Events',
          highestRiskVehicle: highestRiskVehicle.registration,
          highestRiskVehicleScore: highestRiskVehicle.riskScore,
          lowestRiskVehicle: lowestRiskVehicle.registration,
          lowestRiskVehicleScore: lowestRiskVehicle.riskScore,
          highestRiskDriver: highestRiskDriver.name,
          highestRiskDriverScore: highestRiskDriver.riskScore,
          lowestRiskDriver: lowestRiskDriver.name,
          lowestRiskDriverScore: lowestRiskDriver.riskScore,
        },
        vehicles: vehiclesData,
        drivers: driversList,
        devices: devicesData,
        events: eventsList,
        vehicleRiskScores,
        driverRiskScores,
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  }
);

export const fetchChartData = createAsyncThunk(
  'clientDashboard/fetchChartData',
  async (timeFilter = 'day') => {
    try {
      // Fetch chart-specific data
      const [events, analytics] = await Promise.all([
        fleetAPI.getMyEvents({ period: timeFilter }),
        dashboardAPI.getAnalytics
          ? dashboardAPI.getAnalytics({ period: timeFilter })
          : Promise.resolve({ success: false }),
      ]);

      const eventsData = events.success
        ? events.data.events || events.data.results || events.data
        : [];
      const analyticsData = analytics.success ? analytics.data : {};

      // Process events for charts
      const eventsByType = eventsData.reduce((acc, event) => {
        const type = event.event_type || event.type || 'other';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});

      // Process monthly events trend
      const monthlyEvents = {};
      eventsData.forEach((event) => {
        const date = new Date(event.created_at || event.timestamp);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthlyEvents[monthKey] = (monthlyEvents[monthKey] || 0) + 1;
      });

      // Create array of last 6 months
      const last6Months = [];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        last6Months.push({
          month: monthName,
          count: monthlyEvents[monthKey] || 0,
        });
      }

      return {
        eventsByType,
        monthlyEventsTrend: last6Months,
        speedingTrends: analyticsData.speedingTrends || [],
        vehicleRiskTrends: analyticsData.vehicleRiskTrends || [],
        driverRiskTrends: analyticsData.driverRiskTrends || [],
      };
    } catch (error) {
      console.error('Error fetching chart data:', error);
      throw error;
    }
  }
);

const initialState = {
  timeFilter: 'day',
  overview: {
    totalVehicles: 0,
    activeVehicles: 0,
    totalDrivers: 0,
    activeDrivers: 0,
    totalDevices: 0,
    newEventsToday: 0,
    topEventType: 'Speeding',
    highestRiskVehicle: 'N/A',
    highestRiskVehicleScore: 0,
    lowestRiskVehicle: 'N/A',
    lowestRiskVehicleScore: 0,
    highestRiskDriver: 'N/A',
    highestRiskDriverScore: 0,
    lowestRiskDriver: 'N/A',
    lowestRiskDriverScore: 0,
  },
  vehicles: [],
  drivers: [],
  devices: [],
  events: [],
  vehicleRiskScores: [],
  driverRiskScores: [],
  chartData: {
    eventsByType: {},
    monthlyEventsTrend: [],
    speedingTrends: [],
    vehicleRiskTrends: [],
    driverRiskTrends: [],
  },
  loading: false,
  error: null,
};

const clientDashboardSlice = createSlice({
  name: 'clientDashboard',
  initialState,
  reducers: {
    setTimeFilter: (state, action) => {
      state.timeFilter = action.payload;
    },
    updateOverviewData: (state, action) => {
      state.overview = { ...state.overview, ...action.payload };
    },
    clearDashboardData: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch overview
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.overview = action.payload.overview;
        state.vehicles = action.payload.vehicles;
        state.drivers = action.payload.drivers;
        state.devices = action.payload.devices;
        state.events = action.payload.events;
        state.vehicleRiskScores = action.payload.vehicleRiskScores;
        state.driverRiskScores = action.payload.driverRiskScores;
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch chart data
      .addCase(fetchChartData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchChartData.fulfilled, (state, action) => {
        state.loading = false;
        state.chartData = action.payload;
      })
      .addCase(fetchChartData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { setTimeFilter, updateOverviewData, clearDashboardData } =
  clientDashboardSlice.actions;
export default clientDashboardSlice.reducer;
